import styled from "styled-components";

// Shared building blocks for the checkout flow (progress bar + steps 1-3),
// kept in one place so all steps stay visually consistent.

export const Card = styled.section`
  width: 100%;
  box-sizing: border-box;
  background-color: var(--bg-100);
  border: 1px solid var(--bg-300);
  border-radius: var(--border-radius-large);
  box-shadow: var(--shadow-medium);
  padding: var(--spacing-lg);
`;

export const CardTitle = styled.h2`
  margin: 0 0 var(--spacing-md) 0;
  font-size: var(--font-size-base);
  font-weight: 700;
  color: var(--text-100);
`;

export const CardSubtitle = styled.p`
  margin: -6px 0 var(--spacing-md) 0;
  font-size: var(--font-size-small);
  color: var(--text-200);
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--bg-300);
  margin: var(--spacing-lg) 0;
`;

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-md);

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const FullWidthField = styled.div`
  grid-column: 1 / -1;
`;

export const FullWidthSplitField = styled(FullWidthField)`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-md);

  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const Label = styled.label`
  font-size: var(--font-size-small);
  font-weight: 600;
  color: var(--text-200);
`;

export const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 12px var(--spacing-sm);
  font-size: 14px;
  border-radius: var(--border-radius-base);
  border: 1px solid
    ${({ isValid }) => (isValid === false ? "var(--error-color)" : "var(--bg-300)")};
  background-color: ${({ isValid }) =>
    isValid === false ? "#cf66792c" : "var(--bg-100)"};
  color: var(--text-100);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);

  &:focus {
    outline: none;
    border-color: ${({ isValid }) =>
      isValid === false ? "var(--error-color)" : "var(--primary-100)"};
    box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.12);
  }
`;

export const InputSelect = styled.select`
  width: 100%;
  box-sizing: border-box;
  height: 44px;
  padding: 0 var(--spacing-sm);
  border-radius: var(--border-radius-base);
  border: 1px solid var(--bg-300);
  background: var(--bg-100);
  color: var(--text-100);
  font-size: 14px;
  outline: none;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);

  &:focus {
    border-color: var(--primary-100);
    box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.12);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  option[value=""] {
    color: var(--text-200);
  }
  option {
    background: var(--bg-100);
    color: var(--text-100);
  }

  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, var(--text-200) 50%),
    linear-gradient(135deg, var(--text-200) 50%, transparent 50%);
  background-position: calc(100% - 18px) 18px, calc(100% - 12px) 18px;
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
`;

export const PrimaryButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  background-color: ${({ disabled }) =>
    disabled ? "var(--bg-300)" : "var(--primary-100)"};
  color: var(--bg-100);
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border: none;
  border-radius: var(--border-radius-base);
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: background-color var(--transition-fast);

  &:hover {
    background-color: ${({ disabled }) =>
      disabled ? "var(--bg-300)" : "var(--accent-200)"};
  }

  @media (min-width: 768px) {
    width: auto;
    min-width: 240px;
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: var(--spacing-lg);
  margin-bottom: var(--spacing-sm);
`;

// Selectable "radio card" row, used for delivery methods and payment methods.
export const RadioCard = styled.label`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  width: 100%;
  box-sizing: border-box;
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-sm);
  border-radius: var(--border-radius-base);
  border: 1px solid
    ${({ $isSelected }) => ($isSelected ? "var(--primary-100)" : "var(--bg-300)")};
  background-color: ${({ $isSelected, $disabled }) =>
    $disabled ? "var(--bg-200)" : $isSelected ? "var(--primary-300)" : "var(--bg-100)"};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
  transition: border-color var(--transition-fast), background-color var(--transition-fast);

  &:hover {
    border-color: ${({ $disabled }) => ($disabled ? "var(--bg-300)" : "var(--primary-100)")};
  }
`;

export const RadioCircle = styled.span`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid
    ${({ $isSelected }) => ($isSelected ? "var(--primary-100)" : "var(--bg-300)")};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: var(--primary-100);
    transform: scale(${({ $isSelected }) => ($isSelected ? 1 : 0)});
    transition: transform var(--transition-fast);
  }
`;

export const RadioContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  flex-wrap: wrap;
`;

export const RadioTitle = styled.span`
  font-size: var(--font-size-base);
  font-weight: 700;
  color: var(--text-100);
`;

export const RadioDetails = styled.span`
  display: block;
  font-size: var(--font-size-small);
  color: var(--text-200);
  margin-top: 2px;
`;

export const RadioPrice = styled.span`
  font-size: var(--font-size-base);
  font-weight: 700;
  color: var(--text-100);
  white-space: nowrap;
`;

export const InfoBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  width: 100%;
  box-sizing: border-box;
  background-color: var(--primary-300);
  border-radius: var(--border-radius-base);
  padding: var(--spacing-md);
`;

export const InfoBannerLabel = styled.p`
  margin: 0 0 4px 0;
  font-size: var(--font-size-small);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--accent-200);
`;

export const InfoBannerText = styled.p`
  margin: 0;
  font-size: var(--font-size-small);
  color: var(--text-100);
`;
